import api from './client';
import { Timestamp } from 'firebase/firestore';
import type { SyncEmail } from 'types/sync_email';

function toTimestamp(value: any): Timestamp {
  if (!value) return Timestamp.now();
  if (value instanceof Timestamp) return value;
  if (typeof value === 'string') {
    return Timestamp.fromDate(new Date(value));
  }
  if (value.seconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  return Timestamp.now();
}

function apiResponseToSyncEmail(response: any): SyncEmail {
  return {
    ...response,
    received_at: toTimestamp(response.received_at),
    created_at: toTimestamp(response.created_at),
    attachments: response.attachments || [],
    to_emails: response.to_emails || [],
    cc_emails: response.cc_emails || [],
    is_read: response.is_read ?? false,
  };
}

export const syncEmailsApi = {
  list: async (params?: {
    org_id?: string;
    project_id?: string;
    is_read?: boolean;
  }): Promise<SyncEmail[]> => {
    const { data } = await api.get('/sync-emails', { params });
    return (data as any[]).map(apiResponseToSyncEmail);
  },

  get: async (emailId: string): Promise<SyncEmail> => {
    const { data } = await api.get(`/sync-emails/${emailId}`);
    return apiResponseToSyncEmail(data);
  },

  markAsRead: async (emailId: string): Promise<void> => {
    await api.patch(`/sync-emails/${emailId}/read`);
  },

  assignToProject: async (emailId: string, projectId: string): Promise<SyncEmail> => {
    const { data } = await api.patch(`/sync-emails/${emailId}/assign`, { project_id: projectId });
    return apiResponseToSyncEmail(data);
  },

  getUnreadCount: async (params?: { org_id?: string }): Promise<number> => {
    const { data } = await api.get('/sync-emails/unread-count', { params });
    return (data as any).count ?? 0;
  },

  reply: async (emailId: string, body: string, cc?: string[]): Promise<void> => {
    await api.post(`/sync-emails/${emailId}/reply`, { body, cc });
  },
};
